import React, { useEffect } from 'react';
import { useLanguage } from '../../../../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import '../style/product-detail-dialog.css';

interface ProductDetailDialogProps {
  product: {
    id: string;
    productNamePt?: string;
    productNameEn?: string;
    productBrand?: string;
    productCategory?: string;
    categoryId?: number | string;
    productCode?: string;
    productPrice?: string | number;
    productDescription?: string;
    productDescriptionPt?: string;
    productImages?: string[];
  };
  onClose: () => void;
  onAddToWishlist: (productId: string) => void;
}

const ProductDetailDialog: React.FC<ProductDetailDialogProps> = ({
  product,
  onClose,
  onAddToWishlist,
}) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const translation = t as any;
  const store = translation.STORE;

  // Get category display name using CategoryEnum
  const getCategoryDisplayName = (product: any): string => {
    if (!product.categoryId || !(window as any).categoryEnum) {
      return product.productCategory || 'N/A';
    }
    
    try {
      return (window as any).categoryEnum.getCategoryName(product.categoryId, language);
    } catch (_) {
      return product.productCategory || 'N/A';
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleViewDetails = () => {
    onClose();
    navigate(`/produto/${product.id}`);
  };

  const handleAddToWishlist = () => {
    onAddToWishlist(product.id);
    onClose();
  };

  const handleAddToBudget = () => {
    // Store the product data for budget page
    const budgetProduct = {
      id: product.id,
      productNamePt: product.productNamePt,
      productNameEn: product.productNameEn,
      productBrand: product.productBrand,
      productImage: product.productImages?.[0] || '',
      productCode: product.productCode,
      categoryId: product.categoryId
    };

    // Get existing budget products or create new array
    let existingProducts = JSON.parse(localStorage.getItem('budgetProducts') || '[]');
    
    // Check if product already exists
    const exists = existingProducts.some((p: any) => p.id === budgetProduct.id);
    if (!exists) {
      existingProducts.push(budgetProduct);
      localStorage.setItem('budgetProducts', JSON.stringify(existingProducts));
    }

    // Set flag to indicate we're coming from product list with a newly added product
    localStorage.setItem('budgetProductAdded', JSON.stringify({
      productId: budgetProduct.id,
      timestamp: Date.now()
    }));

    onClose();
    navigate('/orcamento');
  };

  const hasImage = product.productImages && product.productImages.length > 0 && product.productImages[0];
  const productImage = hasImage ? product.productImages?.[0] : null;

  return (
    <div className="product-detail-dialog-overlay" onClick={handleOverlayClick}>
      <div className="product-detail-dialog">
        {/* Header */}
        <div className="dialog-header">
          <button
            className="dialog-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <h3 className="dialog-title">
            {product.productNamePt || product.productNameEn || 'Product'}
          </h3>
          <p className="dialog-brand">
            {product.productBrand || 'Brand'}
          </p>
        </div>

        {/* Content */}
        <div className="dialog-content">
          <div className="dialog-grid">
            {/* Image Column */}
            <div className="dialog-image-container">
              {productImage ? (
                <img
                  src={productImage}
                  alt={product.productNamePt || product.productNameEn || 'Product'}
                  className="dialog-product-image"
                />
              ) : (
                <div className="dialog-product-placeholder">
                  <i className="fa fa-solid fa-box"></i>
                </div>
              )}
            </div>

            {/* Details Column */}
            <div className="dialog-details">
              {/* Product Information */}
              <div className="dialog-info-section">
                <h4 className="dialog-section-title">{store.productInformation}</h4>
                
                <div className="dialog-info-item">
                  <strong>{store.brand}:</strong>
                  <span>{product.productBrand || 'N/A'}</span>
                </div>
                
                <div className="dialog-info-item">
                  <strong>{store.category}:</strong>
                  <span>{getCategoryDisplayName(product)}</span>
                </div>
                
                <div className="dialog-info-item">
                  <strong>Code:</strong>
                  <span>{product.productCode || 'N/A'}</span>
                </div>
                
                {product.productPrice && (
                  <div className="dialog-info-item">
                    <strong>Price:</strong>
                    <span className="dialog-price">${product.productPrice}</span>
                  </div>
                )}
              </div>

              {/* Product Description */}
              <div className="dialog-description-section">
                <h4 className="dialog-section-title">{store.description}</h4>
                <p className="dialog-description">
                  {product.productDescription || product.productDescriptionPt || store.noDescriptionAvailable}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="dialog-actions">
                <button
                  className="thm-btn dialog-btn-primary"
                  onClick={handleViewDetails}
                >
                  {store.viewDetails}
                  <span>
                    <i className="icon-next"></i>
                  </span>
                </button>
                
                <button
                  className="thm-btn dialog-btn-secondary"
                  onClick={handleAddToWishlist}
                >
                  {store.addToWishlist}
                  <span>
                    <i className="fa fa-heart"></i>
                  </span>
                </button>
                
                <button
                  className="thm-btn dialog-btn-budget"
                  onClick={handleAddToBudget}
                >
                  {language === 'pt' ? 'Adicionar ao Orçamento' : 'Add to Budget'}
                  <span>
                    <i className="fa fa-file-invoice-dollar"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailDialog;

